import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import { AttachFile, Add, Close } from '@mui/icons-material';
import { tasksAPI } from '../../services/api';

const TaskForm = ({ open, onClose, projectId, task, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'medium',
    status: 'todo',
    reminders: [],
  });
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [customReminderDateTime, setCustomReminderDateTime] = useState('');

  const reminderOptions = [
    { value: '1day', label: '1 Day Before' },
    { value: '2days', label: '2 Days Before' },
    { value: '1week', label: '1 Week Before' },
    { value: '2weeks', label: '2 Weeks Before' },
    { value: '1month', label: '1 Month Before' },
  ];

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        reminders: task.reminders || [],
      });
    } else {
      setFormData({
        title: '',
        description: '',
        deadline: '',
        priority: 'medium',
        status: 'todo',
        reminders: [],
      });
      setFiles([]);
    }
    setCustomReminderDateTime('');
  }, [task, open]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleReminderToggle = (reminderType) => {
    const reminderDate = calculateReminderDate(formData.deadline, reminderType);
    const existingIndex = formData.reminders.findIndex((r) => r.type === reminderType);

    let newReminders;
    if (existingIndex >= 0) {
      newReminders = formData.reminders.filter((_, idx) => idx !== existingIndex);
    } else {
      newReminders = [
        ...formData.reminders,
        {
          type: reminderType,
          reminderDate: reminderDate,
          sent: false,
        },
      ];
    }

    setFormData({ ...formData, reminders: newReminders });
  };

  const calculateReminderDate = (deadline, type) => {
    const date = new Date(deadline);
    switch (type) {
      case '1day':
        date.setDate(date.getDate() - 1);
        break;
      case '2days':
        date.setDate(date.getDate() - 2);
        break;
      case '1week':
        date.setDate(date.getDate() - 7);
        break;
      case '2weeks':
        date.setDate(date.getDate() - 14);
        break;
      case '1month':
        date.setMonth(date.getMonth() - 1);
        break;
      default:
        break;
    }
    return date.toISOString();
  };

  const handleAddCustomReminder = () => {
    if (!customReminderDateTime) {
      setError('Please select a date and time for the custom reminder');
      return;
    }

    const selectedDate = new Date(customReminderDateTime);
    const deadlineDate = new Date(formData.deadline);

    // Validate that custom reminder is before deadline
    if (selectedDate >= deadlineDate) {
      setError('Custom reminder must be before the task deadline');
      return;
    }

    // Check if this exact datetime already exists
    const exists = formData.reminders.some(
      (r) => r.type === 'custom' && new Date(r.reminderDate).getTime() === selectedDate.getTime()
    );

    if (exists) {
      setError('This reminder time already exists');
      return;
    }

    const newReminder = {
      type: 'custom',
      reminderDate: selectedDate.toISOString(),
      sent: false,
    };

    setFormData({
      ...formData,
      reminders: [...formData.reminders, newReminder],
    });

    setCustomReminderDateTime('');
    setError('');
  };

  const handleRemoveCustomReminder = (reminderDate) => {
    const newReminders = formData.reminders.filter(
      (r) => !(r.type === 'custom' && r.reminderDate === reminderDate)
    );
    setFormData({ ...formData, reminders: newReminders });
  };

  const formatCustomReminderLabel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCustomReminders = () => {
    return formData.reminders.filter((r) => r.type === 'custom');
  };

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const taskData = {
        ...formData,
        project: projectId,
      };

      let taskResponse;
      if (task) {
        taskResponse = await tasksAPI.update(task._id, taskData);
      } else {
        taskResponse = await tasksAPI.create(taskData);
      }

      // Upload attachments
      if (files.length > 0 && taskResponse.data.data._id) {
        const fileFormData = new FormData();
        files.forEach((file) => {
          fileFormData.append('files', file);
        });
        await tasksAPI.uploadAttachments(taskResponse.data.data._id, fileFormData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{task ? 'Edit Task' : 'Create New Task'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            margin="normal"
            required
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={4}
            inputProps={{ maxLength: 2000 }}
          />

          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label="Deadline"
              name="deadline"
              type="date"
              value={formData.deadline}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select name="priority" value={formData.priority} onChange={handleChange}>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select name="status" value={formData.status} onChange={handleChange}>
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              Preset Reminders
            </Typography>
            <FormGroup row>
              {reminderOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={formData.reminders.some((r) => r.type === option.value)}
                      onChange={() => handleReminderToggle(option.value)}
                      disabled={!formData.deadline}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Custom Reminder Schedule
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                type="datetime-local"
                value={customReminderDateTime}
                onChange={(e) => setCustomReminderDateTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!formData.deadline}
                fullWidth
                inputProps={{
                  min: new Date().toISOString().slice(0, 16),
                  max: formData.deadline
                    ? new Date(formData.deadline).toISOString().slice(0, 16)
                    : undefined,
                }}
                helperText="Select a specific date and time for reminder"
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddCustomReminder}
                disabled={!customReminderDateTime || !formData.deadline}
                sx={{ minWidth: '120px' }}
              >
                Add
              </Button>
            </Box>

            {getCustomReminders().length > 0 && (
              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                {getCustomReminders().map((reminder, idx) => (
                  <Chip
                    key={idx}
                    label={formatCustomReminderLabel(reminder.reminderDate)}
                    onDelete={() => handleRemoveCustomReminder(reminder.reminderDate)}
                    color="secondary"
                    deleteIcon={<Close />}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box mt={3}>
            <Button variant="outlined" component="label" startIcon={<AttachFile />}>
              Attach Files
              <input
                type="file"
                multiple
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
              />
            </Button>
            {files.length > 0 && (
              <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                {Array.from(files).map((file, idx) => (
                  <Chip key={idx} label={file.name} size="small" />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : task ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskForm;
